'use client';

import { Alert } from '../../../../components/alert';
import {
  BodyDefault,
  LabelDefault,
  MetaDefault,
} from '../../../../components/typography';
import { Checkbox } from '../../../../components/checkbox';
import { FieldWrapper } from '../../../../components/field-wrapper/FieldWrapper';
import { Input } from '../../../../components/input';
import { Switch } from '../../../../components/switch';
import { Tooltip } from '../../../../components/tooltip';
import { Box, HStack, token, VStack } from '@terra/ui-styled-system';
import { Alert01Icon, InformationCircleIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';
import {
  type MfaFormConfig,
  useAddCredentialsDialogStore,
} from '../AddCredentialsDialogStore';
import { hasCredentialAuthenticationMethod } from '../utils';
import { useShallow } from 'zustand/shallow';

type MfaProviderType = MfaFormConfig['providerType'];
const MFA_PROVIDERS = [
  {
    type: 'EMAIL' as MfaProviderType,
    titleKey: 'providers.email.title',
    descriptionKey: 'providers.email.description',
  },
  {
    type: 'SMS' as MfaProviderType,
    titleKey: 'providers.sms.title',
    descriptionKey: 'providers.sms.description',
  },
  {
    type: 'OTP' as MfaProviderType,
    titleKey: 'providers.otp.title',
    descriptionKey: 'providers.otp.description',
  },
  {
    type: 'WEBHOOK' as MfaProviderType,
    titleKey: 'providers.webhook.title',
    descriptionKey: 'providers.webhook.description',
  },
] as const;

export const MultiFactorAuthenticationStep = () => {
  const t = useTranslations(
    'designSystem.credentials.addDialog.steps.multiFactorAuthentication',
  );
  const tCard = useTranslations('designSystem.credentials.card');
  const {
    credential,
    formMeta,
    setFormMeta,
    setCredential,
    validationErrors,
    setValidationErrors,
  } = useAddCredentialsDialogStore(
    useShallow((state) => ({
      credential: state.credential,
      formMeta: state.formMeta,
      setFormMeta: state.setFormMeta,
      setCredential: state.setCredential,
      validationErrors: state.validationErrors,
      setValidationErrors: state.setValidationErrors,
    })),
  );
  const { mfaEnabled, mfaConfigs, loginType } = formMeta;
  const isEmailLoginType = loginType === 'email';

  const emailMfaSelected = mfaConfigs.some(
    (config) => config.providerType === 'EMAIL' && config.enabled,
  );
  const showMailboxCreatedOnSave =
    !credential.id && emailMfaSelected && !credential.generatedMailboxAddress;

  const mfaTypeLabel = useMemo(() => {
    const enabledTypes = MFA_PROVIDERS.filter((provider) =>
      mfaConfigs.some(
        (config) => config.providerType === provider.type && config.enabled,
      ),
    ).map((provider) => t(provider.titleKey));

    return enabledTypes.join(', ');
  }, [mfaConfigs, t]);

  useEffect(() => {
    if (!validationErrors.authentication) {
      return;
    }

    if (hasCredentialAuthenticationMethod(credential)) {
      setValidationErrors({ authentication: '' });
    }
  }, [credential, setValidationErrors, validationErrors.authentication]);
  const isProviderEnabled = (providerType: MfaProviderType): boolean =>
    mfaConfigs.some((c) => c.providerType === providerType && c.enabled);
  const syncMfaToCredential = (enabled: boolean, configs: MfaFormConfig[]) => {
    if (!enabled) {
      setCredential({ mfaConfig: [] });

      return;
    }

    const mfaConfig = configs
      .filter(({ enabled: configEnabled }) => configEnabled)
      .map(({ id, providerType, totpSecret }) => ({
        ...(id ? { id } : {}),
        enabled: true,
        providerType,
        ...(totpSecret ? { totpSecret } : {}),
      }));

    setCredential({ mfaConfig });
  };

  const previousLoginTypeRef = useRef(loginType);

  useEffect(() => {
    const previousLoginType = previousLoginTypeRef.current;

    previousLoginTypeRef.current = loginType;

    if (loginType === 'email' || previousLoginType !== 'email') {
      return;
    }

    const { formMeta: currentFormMeta } =
      useAddCredentialsDialogStore.getState();
    const emailEnabled = currentFormMeta.mfaConfigs.some(
      (config) => config.providerType === 'EMAIL' && config.enabled,
    );

    if (!emailEnabled) {
      return;
    }

    const updatedConfigs = currentFormMeta.mfaConfigs.map((config) =>
      config.providerType === 'EMAIL' ? { ...config, enabled: false } : config,
    );

    setFormMeta({ mfaConfigs: updatedConfigs });

    if (!currentFormMeta.mfaEnabled) {
      setCredential({ mfaConfig: [] });

      return;
    }

    setCredential({
      mfaConfig: updatedConfigs
        .filter(({ enabled }) => enabled)
        .map(({ id, providerType, totpSecret }) => ({
          ...(id ? { id } : {}),
          enabled: true,
          providerType,
          ...(totpSecret ? { totpSecret } : {}),
        })),
    });
  }, [loginType, setCredential, setFormMeta]);

  const handleGlobalToggle = (checked: boolean) => {
    if (!checked) {
      const updatedConfigs = mfaConfigs.map((config) => ({
        ...config,
        enabled: false,
      }));

      setFormMeta({ mfaEnabled: false, mfaConfigs: updatedConfigs });
      syncMfaToCredential(false, updatedConfigs);
      setValidationErrors({ totpSecret: '' });
    } else {
      setFormMeta({ mfaEnabled: true });
      syncMfaToCredential(true, mfaConfigs);
    }
  };

  const handleProviderToggle = (providerType: MfaProviderType) => {
    if (providerType === 'EMAIL' && !isEmailLoginType) {
      return;
    }

    const existing = mfaConfigs.find((c) => c.providerType === providerType);
    let updatedConfigs: MfaFormConfig[];

    if (existing) {
      updatedConfigs = mfaConfigs.map((config) =>
        config.providerType === providerType
          ? { ...config, enabled: !config.enabled }
          : config,
      );
    } else {
      const newConfig: MfaFormConfig = {
        enabled: true,
        providerType,
        ...(providerType === 'OTP' && { totpSecret: '' }),
      };

      updatedConfigs = [...mfaConfigs, newConfig];
    }

    setFormMeta({ mfaConfigs: updatedConfigs });
    syncMfaToCredential(mfaEnabled, updatedConfigs);
    if (providerType === 'OTP' && isProviderEnabled('OTP')) {
      setValidationErrors({ totpSecret: '' });
    }
  };

  const handleTotpSecretChange = (secret: string) => {
    const existing = mfaConfigs.find((c) => c.providerType === 'OTP');
    let updatedConfigs: MfaFormConfig[];

    if (existing) {
      updatedConfigs = mfaConfigs.map((config) =>
        config.providerType === 'OTP'
          ? { ...config, totpSecret: secret || undefined }
          : config,
      );
    } else {
      updatedConfigs = [
        ...mfaConfigs,
        {
          enabled: true,
          providerType: 'OTP' as MfaProviderType,
          totpSecret: secret || undefined,
        },
      ];
    }

    setFormMeta({ mfaConfigs: updatedConfigs });
    syncMfaToCredential(mfaEnabled, updatedConfigs);
    setValidationErrors({ totpSecret: '' });
  };

  const otpConfig = mfaConfigs.find((c) => c.providerType === 'OTP');

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      {validationErrors.authentication ? (
        <Alert.Root
          bg="bg.vulnerability.vulnerable"
          borderColor="border.vulnerability.critical"
          px="4"
          py="2"
          gap={4}
          borderRadius="md"
          alignItems="center"
        >
          <Alert.Icon asChild>
            <Alert01Icon
              size={20}
              color={token('colors.fg.vulnerability.vulnerable')}
            />
          </Alert.Icon>
          <Alert.Content>
            <Alert.Description fontSize="sm" color="fg.neutral.default">
              {validationErrors.authentication}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <FieldWrapper label={tCard('tabs.mfa.title')}>
        <Switch
          size="sm"
          checked={mfaEnabled}
          onCheckedChange={({ checked }) => handleGlobalToggle(checked)}
        >
          <BodyDefault as="span" color="fg.neutral.muted">
            {mfaEnabled ? t('enabled') : t('disabled')}
          </BodyDefault>
        </Switch>
      </FieldWrapper>

      {mfaEnabled && (
        <VStack alignItems="stretch" gap={2}>
          <FieldWrapper label={t('mfaType')}>
            <BodyDefault color="fg.neutral.default">
              {mfaTypeLabel || '-'}
            </BodyDefault>
          </FieldWrapper>
          <LabelDefault as="span" color="fg.neutral.default">
            {t('selectProviders')}
          </LabelDefault>

          {showMailboxCreatedOnSave ? (
            <Alert.Root
              bg="bg.neutral.light"
              borderColor="border.neutral.subtle"
              px="4"
              py="2"
              gap={4}
              borderRadius="md"
              alignItems="center"
            >
              <Alert.Icon asChild>
                <InformationCircleIcon size={20} />
              </Alert.Icon>
              <Alert.Content>
                <Alert.Description fontSize="sm" color="fg.neutral.default">
                  {t('infoMessage')}
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          ) : null}

          {MFA_PROVIDERS.map((provider) => {
            const isSelected = isProviderEnabled(provider.type);
            const isEmailDisabled =
              provider.type === 'EMAIL' && !isEmailLoginType;
            const providerRow = (
              <HStack
                gap={4}
                alignItems="center"
                px={4}
                py={3}
                borderWidth="1px"
                borderColor={
                  isSelected ? 'border.neutral.focus' : 'border.neutral.subtle'
                }
                borderRadius="md"
                bg="bg.neutral.default"
                cursor={isEmailDisabled ? 'not-allowed' : 'pointer'}
                boxShadow={isSelected ? 'xs' : undefined}
                opacity={isEmailDisabled ? 0.6 : undefined}
                onClick={() => handleProviderToggle(provider.type)}
              >
                <Checkbox
                  size="sm"
                  checked={isSelected}
                  disabled={isEmailDisabled}
                  pointerEvents="none"
                />
                <VStack alignItems="flex-start" gap={0} flex={1}>
                  <BodyDefault fontWeight="medium">
                    {t(provider.titleKey)}
                  </BodyDefault>
                  <MetaDefault color="fg.neutral.muted">
                    {t(provider.descriptionKey)}
                  </MetaDefault>
                </VStack>
              </HStack>
            );

            return (
              <Box key={provider.type} w="full">
                {isEmailDisabled ? (
                  <Tooltip
                    title={t('providers.email.disabledTooltip')}
                    positioning={{ placement: 'top', strategy: 'fixed' }}
                  >
                    <Box w="full">{providerRow}</Box>
                  </Tooltip>
                ) : (
                  providerRow
                )}

                {provider.type === 'OTP' && isSelected && (
                  <Box mt={2}>
                    <FieldWrapper
                      label={t('totpSecret.label')}
                      isRequired
                      helperText={t('totpSecret.hint')}
                      error={validationErrors.totpSecret}
                    >
                      <Input
                        size="sm"
                        placeholder={t('totpSecret.placeholder')}
                        value={otpConfig?.totpSecret ?? ''}
                        onChange={(e) => handleTotpSecretChange(e.target.value)}
                      />
                    </FieldWrapper>
                  </Box>
                )}
              </Box>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};
